/**
 * MobileHeroClaimCTA — Phase 4A launch fix.
 *
 * A compact Google-first Claim button that renders ABOVE THE FOLD on
 * mobile viewports (≤ md breakpoint) for unclaimed certificates. The
 * larger "Claim My Profile" card that lives further down the page
 * remains the emotional payoff — this compact CTA guarantees that a
 * collector opening their certificate link on a phone can start the
 * Google-based claim without scrolling.
 *
 * Hidden on:
 *  - Desktop (md and up) — the two-column layout already shows the
 *    primary claim card in the first viewport.
 *  - Claimed certificates — the celebration takes over.
 *
 * Uses the same auth-preserving destinations as ClaimCTA:
 *   * Logged-out              → /register?claim={certNumber}
 *   * Logged-in collector     → /dashboard/claim?cert={certNumber}
 * Google is the primary action; email sign-in is a secondary link.
 */
import React from "react";
import { Link } from "react-router-dom";
import { useCollectorAuth } from "@/context/CollectorAuthContext";

// Inline SVG so we don't pull in another icon dependency.
function GoogleGlyph() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#EA4335" d="M12 10.5v3.5h4.85c-.2 1.15-1.4 3.4-4.85 3.4-2.9 0-5.3-2.4-5.3-5.4S9.1 6.6 12 6.6c1.7 0 2.85.7 3.5 1.3l2.4-2.3C16.4 4.05 14.4 3 12 3c-4.95 0-9 4.05-9 9s4.05 9 9 9c5.2 0 8.65-3.65 8.65-8.8 0-.6-.05-1.05-.15-1.7H12z"/>
    </svg>
  );
}

export default function MobileHeroClaimCTA({ certNumber, isClaimed }) {
  const { collector } = useCollectorAuth();
  if (isClaimed) return null;

  // Preserve claim intent across the OAuth roundtrip. Google is
  // launched by the destination route (register/login/dashboard) via
  // SocialLoginButtons — we never place the plaintext claim code in
  // any URL here.
  const primary = collector
    ? `/dashboard/claim?cert=${encodeURIComponent(certNumber)}`
    : `/register?claim=${encodeURIComponent(certNumber)}`;
  const signInHref = `/login?claim=${encodeURIComponent(certNumber)}`;

  return (
    <div
      className="md:hidden mt-6 mx-auto max-w-md"
      data-testid="mobile-hero-claim-cta"
    >
      <Link
        to={primary}
        data-testid="mobile-hero-claim-primary"
        className={
          "flex w-full items-center justify-center gap-2.5 " +
          "rounded-2xl bg-white text-[var(--ink)] " +
          "border-2 border-[var(--collector-gold)] " +
          "font-display font-bold tracking-tight " +
          "text-base px-5 py-3.5 " +
          "shadow-[0_10px_24px_rgba(200,162,77,0.35)] " +
          "active:translate-y-[1px] " +
          "focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[var(--collector-gold)]/60"
        }
      >
        <GoogleGlyph />
        <span>Sign in with Google to Claim</span>
      </Link>
      <div className="mt-2 text-center">
        <Link
          to={signInHref}
          data-testid="mobile-hero-claim-secondary"
          className="text-[11px] uppercase tracking-widest text-white/60 hover:text-white/90"
        >
          Or sign in with email
        </Link>
      </div>
    </div>
  );
}
